import React, { useState } from 'react';

import styled from '@emotion/styled';
import { Table, Tbody, Thead, Tr } from '@lightspeed/cirrus-table';
import { Alert } from '@lightspeed/flame/Alert';
import { Bone } from '@lightspeed/flame/Bone';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Heading3, TextLink } from '@lightspeed/flame/Text';
import { useInfiniteQuery } from '@tanstack/react-query';

import { NuOrderAlert } from 'pages/views/inventory/B2B/NuOrderAlert';
import { getStorageNotificationTabKey } from 'root/apps/components/Notifications/utils';
import { createReactRenderer } from 'root/apps/helpers/react-renderer';

import { B2BBanner } from './B2BBanner';
import { ListingTD, ListingTH, StyledAlert } from './B2BItemsListing.styles';
import { B2BImportButton } from './Import/B2BImportButton';

const B2BItemsListing = ({
  description,
  purchaseID,
  vendorID,
  hasNuorderB2bCatalogBannerFF,
  isLinkedToNuorder,
}) => {
  // We're using a ref to prevent re-renders when the import is in progress
  const importInProgressRef = React.useRef(false);

  const observer = React.useRef();
  const tabKey = 'b2b_matches';
  const [showNuorderB2bCatalogBanner, setShowNuorderB2bCatalogBanner] = useState(false);
  const fetchB2BListing = async ({ pageParam = null }) => {
    const requestBody = {
      keyword: description,
    };
    if (pageParam !== null) {
      requestBody.scrollID = pageParam;
    }
    const res = await fetch(`/admin/item/b2b-catalog/search`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
    if (!res.ok) {
      throw new Error('Network response was not ok');
    }
    const data = await res.json();
    return { items: data.data || [], nextScrollId: data.scrollId };
  };

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: ['b2blisting', description],
    queryFn: fetchB2BListing,
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.nextScrollId;
    },
    enabled: !!description,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    staleTime: 0, // no caching until necessary
  });

  const lastElementRef = React.useCallback(
    (node) => {
      if (isLoading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetching) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [fetchNextPage, hasNextPage, isFetching, isLoading]
  );

  React.useEffect(() => {
    if (hasNuorderB2bCatalogBannerFF === 'true') {
      const notifications = window.localStorage.getItem(getStorageNotificationTabKey(tabKey));
      if (notifications > 0 && isLinkedToNuorder === 'false') {
        setShowNuorderB2bCatalogBanner(true);
      }
    }
  }, [hasNuorderB2bCatalogBannerFF, isLinkedToNuorder]);

  // We save the vendor import alert state to the session storage
  // Because we want to remind the user when they re-visit the app
  const handleAlertClose = () => {
    window.sessionStorage.setItem('b2b-catalog-import-alert-closed', 'true');
  };

  const handleBannerClose = () => {
    window.sessionStorage.setItem('b2b-alert-banner', 'true');
  };

  // Get the alert close status from sessionStorage
  const alertClosed = window.sessionStorage.getItem('b2b-catalog-import-alert-closed');

  // Get the Banner close status from sessionStorage
  const bannerClosed = window.sessionStorage.getItem('b2b-alert-banner');

  React.useEffect(() => {
    return () => {
      observer.current?.disconnect();
    };
  }, []);

  const B2BItems = React.useMemo(() => {
    return data?.pages.reduce((acc, page) => {
      return [...acc, ...page.items];
    }, []);
  }, [data]);

  if (error) {
    window.showNotifier('warning', 'Unable to load B2B matches. Refresh the page and try again');
    return null;
  }

  if (!description) {
    return (
      <>
        {hasNuorderB2bCatalogBannerFF && showNuorderB2bCatalogBanner && (
          <NuOrderAlert tabKey={tabKey} />
        )}
        {!hasNuorderB2bCatalogBannerFF && !bannerClosed && <B2BBanner />}
        <div className="no_results">Please enter an item description</div>
      </>
    );
  }

  if (!isLoading && !B2BItems?.length) {
    return <div className="no_results">No B2B catalog items found</div>;
  }

  return (
    <>
      {hasNuorderB2bCatalogBannerFF && showNuorderB2bCatalogBanner && (
        <NuOrderAlert tabKey={tabKey} />
      )}
      {!hasNuorderB2bCatalogBannerFF && !bannerClosed && (
        <B2BBanner onBannerClose={handleBannerClose} />
      )}
      {!alertClosed && bannerClosed && (
        <Alert type="info" m={1} onClose={handleAlertClose} data-test="vendor-import-banner">
          B2B catalog import does not include vendor information. Please assign your preferred
          vendor after you import.
        </Alert>
      )}
      <Table>
        <Thead>
          <Tr>
            <ListingTH />
            <ListingTH width="70px">ITEM</ListingTH>
            <ListingTH />
            <ListingTH>UPC</ListingTH>
            <ListingTH>MSRP</ListingTH>
            <ListingTH>BRAND</ListingTH>
          </Tr>
        </Thead>
        <Tbody>
          {isLoading &&
            Array.from({ length: 5 }).map((_, trIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <Tr key={trIndex}>
                {Array.from({ length: 6 }).map((_element, tdIndex) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <ListingTD key={`${trIndex}_${tdIndex}`}>
                    <Bone height="25px" />
                  </ListingTD>
                ))}
              </Tr>
            ))}
          {!isLoading &&
            B2BItems &&
            B2BItems.map((item) => {
              const { currency, amount } =
                item?.pricing?.find((price) => price.type.toLowerCase() === 'msrp') || {};

              const msrp = Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency || 'USD',
              }).format(amount);

              return (
                <Tr key={`${item.upc}_${item.id}`} ref={lastElementRef}>
                  <ListingTD>
                    <B2BImportButton
                      b2bItem={item}
                      purchaseID={purchaseID}
                      vendorID={vendorID}
                      importInProgressRef={importInProgressRef}
                    >
                      Import
                    </B2BImportButton>
                  </ListingTD>
                  <ListingTD>
                    {item.images?.[0]?.image_url ? (
                      <img
                        src={item.images?.[0]?.image_url}
                        alt={`${item.color} ${item.size} ${item.brand_name} ${item.name}`}
                      />
                    ) : (
                      // eslint-disable-next-line jsx-a11y/img-redundant-alt
                      <img src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/b2b/no_image_50.svg" alt="No product image" />
                    )}
                  </ListingTD>
                  <ListingTD>{item.name}</ListingTD>
                  <ListingTD>{item.upc}</ListingTD>
                  <ListingTD>{msrp !== '$NaN' ? msrp : 'N/A'}</ListingTD>
                  <ListingTD>{item.brand_name}</ListingTD>
                </Tr>
              );
            })}
        </Tbody>
      </Table>
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: B2BItemsListing,
  selector: '[data-react-root="b2b-catalog"]',
});

history.onChangeState(
  [
    {
      name: ['item.listings.items', 'purchase.listings.select_items'],
      form_name: 'listing',
      id: undefined,
    },
  ],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default B2BItemsListing;
